import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageSquare, Trash2, Reply, Heart, Send, X, AlertCircle, Star, Share2, Upload, Search, Eye, EyeOff, Lightbulb } from 'lucide-react';

interface Annotation {
  id: string;
  text: string;
  position: {
    x: number;
    y: number;
    element: string;
    xpath: string;
  };
  content: string;
  type: 'note' | 'question' | 'suggestion' | 'criticism' | 'praise' | 'link' | 'image';
  author: string;
  authorId: string;
  createdAt: Date;
  updatedAt: Date;
  isPublic: boolean;
  isResolved: boolean;
  likes: number;
  isLiked: boolean;
  replies: AnnotationReply[];
  tags: string[];
  color: string;
  priority: 'low' | 'medium' | 'high';
  status: 'draft' | 'published' | 'archived';
}

interface AnnotationReply {
  id: string;
  content: string;
  author: string;
  authorId: string;
  createdAt: Date;
  likes: number;
  isLiked: boolean;
}

interface InlineAnnotationsProps {
  contentRef: React.RefObject<HTMLElement | null>;
  userId: string;
  onAnnotationCreate?: (annotation: Annotation) => void;
  onAnnotationUpdate?: (annotation: Annotation) => void;
  onAnnotationDelete?: (annotationId: string) => void;
  onAnnotationReply?: (annotationId: string, reply: AnnotationReply) => void;
  className?: string;
}

const InlineAnnotations: React.FC<InlineAnnotationsProps> = ({
  contentRef,
  userId,
  onAnnotationCreate,
  onAnnotationUpdate,
  onAnnotationDelete,
  onAnnotationReply,
  className = ''
}) => {
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [showAnnotationForm, setShowAnnotationForm] = useState(false);
  const [annotationPosition, setAnnotationPosition] = useState({ x: 0, y: 0 });
  const [selectedElement, setSelectedElement] = useState<HTMLElement | null>(null);
  const [annotationContent, setAnnotationContent] = useState('');
  const [annotationType, setAnnotationType] = useState<Annotation['type']>('note');
  const [annotationColor, setAnnotationColor] = useState('#3b82f6');
  const [annotationPriority, setAnnotationPriority] = useState<Annotation['priority']>('medium');
  const [editingAnnotation, setEditingAnnotation] = useState<Annotation | null>(null);
  const [showReplies, setShowReplies] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'type' | 'likes' | 'priority'>('date');

  const annotationFormRef = useRef<HTMLDivElement>(null);
  const replyInputRef = useRef<HTMLTextAreaElement>(null);

  const annotationTypes = [
    { id: 'note', name: 'Note', icon: MessageSquare, color: '#3b82f6' },
    { id: 'question', name: 'Question', icon: MessageSquare, color: '#f59e0b' },
    { id: 'suggestion', name: 'Suggestion', icon: Lightbulb, color: '#10b981' },
    { id: 'criticism', name: 'Criticism', icon: AlertCircle, color: '#ef4444' },
    { id: 'praise', name: 'Praise', icon: Star, color: '#f59e0b' },
    { id: 'link', name: 'Link', icon: Share2, color: '#8b5cf6' },
    { id: 'image', name: 'Image', icon: Upload, color: '#06b6d4' }
  ];

  const priorityColors = {
    low: '#10b981',
    medium: '#f59e0b',
    high: '#ef4444'
  };

  // Handle element click for annotation placement
  const handleElementClick = useCallback((event: MouseEvent) => {
    if (!showAnnotations) return;
    
    const target = event.target as HTMLElement;
    if (target.closest('.annotation-marker') || target.closest('.annotation-form')) return;
    
    setSelectedElement(target);
    setAnnotationPosition({ x: event.clientX, y: event.clientY });
    setShowAnnotationForm(true);
    setAnnotationContent('');
    setAnnotationType('note');
  }, [showAnnotations]);

  // Create annotation
  const createAnnotation = useCallback(() => {
    if (!selectedElement || !annotationContent.trim()) return;

    const annotation: Annotation = {
      id: Date.now().toString(),
      text: selectedElement.textContent?.substring(0, 100) || '',
      position: {
        x: annotationPosition.x,
        y: annotationPosition.y,
        element: selectedElement.tagName,
        xpath: getXPath(selectedElement)
      },
      content: annotationContent,
      type: annotationType,
      author: 'Current User',
      authorId: userId,
      createdAt: new Date(),
      updatedAt: new Date(),
      isPublic: true,
      isResolved: false,
      likes: 0,
      isLiked: false,
      replies: [],
      tags: [],
      color: annotationColor,
      priority: annotationPriority,
      status: 'published'
    };

    setAnnotations(prev => [...prev, annotation]);
    onAnnotationCreate?.(annotation);
    
    setShowAnnotationForm(false);
    setAnnotationContent('');
    setSelectedElement(null);
  }, [selectedElement, annotationContent, annotationPosition, annotationType, annotationColor, annotationPriority, userId, onAnnotationCreate]);

  // Update annotation
  const updateAnnotation = useCallback((annotationId: string, updates: Partial<Annotation>) => {
    setAnnotations(prev => prev.map(a => 
      a.id === annotationId 
        ? { ...a, ...updates, updatedAt: new Date() }
        : a
    ));
    
    const updatedAnnotation = annotations.find(a => a.id === annotationId);
    if (updatedAnnotation) {
      onAnnotationUpdate?.({ ...updatedAnnotation, ...updates });
    }
  }, [annotations, onAnnotationUpdate]);

  // Delete annotation
  const deleteAnnotation = useCallback((annotationId: string) => {
    setAnnotations(prev => prev.filter(a => a.id !== annotationId));
    onAnnotationDelete?.(annotationId);
  }, [onAnnotationDelete]);

  // Add reply
  const addReply = useCallback((annotationId: string) => {
    if (!replyContent.trim()) return;

    const reply: AnnotationReply = {
      id: Date.now().toString(),
      content: replyContent,
      author: 'Current User',
      authorId: userId,
      createdAt: new Date(),
      likes: 0,
      isLiked: false
    };

    setAnnotations(prev => prev.map(a => 
      a.id === annotationId 
        ? { ...a, replies: [...a.replies, reply] }
        : a
    ));
    
    onAnnotationReply?.(annotationId, reply);
    setReplyContent('');
    setReplyingTo(null);
  }, [replyContent, userId, onAnnotationReply]);

  // Get XPath for element
  const getXPath = (element: HTMLElement): string => {
    const path: string[] = [];
    while (element && element.nodeType === Node.ELEMENT_NODE) {
      let index = 0;
      let sibling = element.previousSibling;
      while (sibling) {
        if (sibling.nodeType === Node.ELEMENT_NODE && sibling.nodeName === element.nodeName) {
          index++;
        }
        sibling = sibling.previousSibling;
      }
      
      const tagName = element.nodeName.toLowerCase();
      const pathIndex = index > 0 ? `[${index + 1}]` : '';
      path.unshift(tagName + pathIndex);
      element = element.parentElement!;
    }
    
    return '/' + path.join('/');
  };

  // Render annotation markers
  useEffect(() => {
    if (!contentRef.current || !showAnnotations) return;

    // Clear existing markers
    const existingMarkers = contentRef.current.querySelectorAll('.annotation-marker');
    existingMarkers.forEach(marker => marker.remove());

    // Add markers for annotations
    annotations.forEach(annotation => {
      if (annotation.status === 'published' && (filterType === 'all' || annotation.type === filterType)) {
        addAnnotationMarker(annotation);
      }
    });
  }, [annotations, showAnnotations, filterType, contentRef]);

  // Add annotation marker to content
  const addAnnotationMarker = (annotation: Annotation) => {
    if (!contentRef.current) return;

    try {
      const xpathResult = document.evaluate(
        annotation.position.xpath,
        contentRef.current,
        null,
        XPathResult.FIRST_ORDERED_NODE_TYPE,
        null
      );
      
      const element = xpathResult.singleNodeValue as HTMLElement;
      if (!element) return;

      const marker = document.createElement('span');
      marker.className = 'annotation-marker';
      marker.style.cssText = `
        position: relative;
        display: inline-block;
        cursor: pointer;
        border-bottom: 2px dotted ${annotation.color};
        background-color: ${annotation.color}20;
        padding: 1px 2px;
        border-radius: 2px;
        margin: 0 1px;
      `;
      marker.dataset.annotationId = annotation.id;
      
      // Add click handler
      marker.addEventListener('click', (e) => {
        e.stopPropagation();
        setEditingAnnotation(annotation);
        setShowReplies(annotation.id);
      });
      
      // Wrap the element content
      const parent = element.parentNode;
      if (parent) {
        const wrapper = document.createElement('span');
        wrapper.appendChild(marker);
        parent.insertBefore(wrapper, element);
        wrapper.appendChild(element);
      }
    } catch (error) {
      console.error('Error adding annotation marker:', error);
    }
  };

  // Event listeners
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.addEventListener('click', handleElementClick);
      
      return () => {
        if (contentRef.current) {
          contentRef.current.removeEventListener('click', handleElementClick);
        }
      };
    }
  }, [handleElementClick, contentRef]);

  // Close form when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (annotationFormRef.current && !annotationFormRef.current.contains(event.target as Node)) {
        setShowAnnotationForm(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filtered and sorted annotations
  const filteredAnnotations = annotations
    .filter(annotation => {
      const matchesType = filterType === 'all' || annotation.type === filterType;
      const matchesStatus = filterStatus === 'all' || annotation.status === filterStatus;
      const matchesSearch = searchQuery === '' || 
        annotation.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
        annotation.text.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesType && matchesStatus && matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'type':
          return a.type.localeCompare(b.type);
        case 'likes':
          return b.likes - a.likes;
        case 'priority':
          const priorityOrder = { high: 3, medium: 2, low: 1 };
          return priorityOrder[b.priority] - priorityOrder[a.priority];
        default:
          return 0;
      }
    });

  return (
    <div className={`inline-annotations-container ${className}`}>
      {/* Annotation Form */}
      {showAnnotationForm && (
        <div
          ref={annotationFormRef}
          className="fixed z-50 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4 min-w-[400px]"
          style={{
            top: annotationPosition.y - 200,
            left: Math.min(annotationPosition.x, window.innerWidth - 420)
          }}
        >
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 dark:text-white">Add Annotation</h3>
              <button
                onClick={() => setShowAnnotationForm(false)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            {/* Annotation Type */}
            <div>
              <label className="block text-sm font-medium mb-2">Type</label>
              <div className="grid grid-cols-4 gap-2">
                {annotationTypes.map(type => (
                  <button
                    key={type.id}
                    onClick={() => setAnnotationType(type.id as Annotation['type'])}
                    className={`flex items-center gap-2 p-2 rounded-lg border transition-colors ${
                      annotationType === type.id
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <type.icon className="w-4 h-4" style={{ color: type.color }} />
                    <span className="text-xs font-medium">{type.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium mb-2">Priority</label>
              <div className="flex gap-2">
                {(['low', 'medium', 'high'] as const).map(priority => (
                  <button
                    key={priority}
                    onClick={() => setAnnotationPriority(priority)}
                    className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                      annotationPriority === priority
                        ? 'text-white'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                    style={{
                      backgroundColor: annotationPriority === priority ? priorityColors[priority] : 'transparent'
                    }}
                  >
                    {priority.charAt(0).toUpperCase() + priority.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-medium mb-2">Annotation</label>
              <textarea
                value={annotationContent}
                onChange={(e) => setAnnotationContent(e.target.value)}
                placeholder="Add your annotation..."
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                rows={3}
                autoFocus
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowAnnotationForm(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={createAnnotation}
                disabled={!annotationContent.trim()}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                Add Annotation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Annotations Panel */}
      <div className="fixed right-0 top-0 h-full w-96 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-lg z-40">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Annotations</h3>
            <button
              onClick={() => setShowAnnotations(!showAnnotations)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              {showAnnotations ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
            </button>
          </div>
          
          {/* Filters */}
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search annotations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                <option value="all">All Types</option>
                {annotationTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
              
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                <option value="all">All Status</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-3">
            {filteredAnnotations.map(annotation => {
              const type = annotationTypes.find(t => t.id === annotation.type);
              return (
                <div
                  key={annotation.id}
                  className="p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div 
                      className="w-4 h-4 rounded-full mt-1 flex-shrink-0" 
                      style={{ backgroundColor: annotation.color }}
                    />
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        {type && <type.icon className="w-4 h-4" style={{ color: type.color }} />}
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                          {type?.name}
                        </span>
                        <span 
                          className="text-xs px-2 py-1 rounded-full text-white"
                          style={{ backgroundColor: priorityColors[annotation.priority] }}
                        >
                          {annotation.priority}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(annotation.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      
                      <p className="text-sm text-gray-900 dark:text-white mb-2">
                        {annotation.content}
                      </p>
                      
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            updateAnnotation(annotation.id, { isLiked: !annotation.isLiked, likes: annotation.isLiked ? annotation.likes - 1 : annotation.likes + 1 });
                          }}
                          className={`p-1 rounded transition-colors ${
                            annotation.isLiked 
                              ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20' 
                              : 'text-gray-400 hover:text-red-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          <Heart className={`w-4 h-4 ${annotation.isLiked ? 'fill-current' : ''}`} />
                        </button>
                        
                        <span className="text-xs text-gray-500">{annotation.likes}</span>
                        
                        <button
                          onClick={() => setShowReplies(showReplies === annotation.id ? null : annotation.id)}
                          className="p-1 text-gray-400 hover:text-blue-500 hover:bg-gray-50 dark:hover:bg-gray-700 rounded transition-colors"
                        >
                          <MessageSquare className="w-4 h-4" />
                        </button>
                        
                        <span className="text-xs text-gray-500">{annotation.replies.length}</span>
                        
                        <button
                          onClick={() => deleteAnnotation(annotation.id)}
                          className="p-1 text-gray-400 hover:text-red-500 hover:bg-gray-50 dark:hover:bg-gray-700 rounded transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      
                      {/* Replies */}
                      {showReplies === annotation.id && (
                        <div className="mt-3 space-y-2">
                          {annotation.replies.map(reply => (
                            <div key={reply.id} className="p-2 bg-gray-50 dark:bg-gray-700 rounded text-sm">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-gray-900 dark:text-white">{reply.author}</span>
                                <span className="text-xs text-gray-500">{new Date(reply.createdAt).toLocaleDateString()}</span>
                              </div>
                              <p className="text-gray-700 dark:text-gray-300">{reply.content}</p>
                            </div>
                          ))}
                          
                          {/* Add Reply */}
                          <div className="flex gap-2">
                            <textarea
                              ref={replyInputRef}
                              value={replyContent}
                              onChange={(e) => setReplyContent(e.target.value)}
                              placeholder="Add a reply..."
                              className="flex-1 p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm resize-none"
                              rows={2}
                            />
                            <button
                              onClick={() => addReply(annotation.id)}
                              disabled={!replyContent.trim()}
                              className="px-3 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm"
                            >
                              <Send className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            
            {filteredAnnotations.length === 0 && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No annotations found</p>
                <p className="text-sm">Click on content to add annotations</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InlineAnnotations;
